import React, { createContext, useState, useContext, useEffect, Component, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import './App.css';

// Global Error Boundary - prevents blank pages on any crash
class GlobalErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null, errorInfo: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, errorInfo) { this.setState({ errorInfo }); console.error('App crash:', error, errorInfo); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#f8fafc',fontFamily:'system-ui'}}>
          <div style={{textAlign:'center',maxWidth:'500px',padding:'40px'}}>
            <div style={{fontSize:'48px',marginBottom:'16px'}}>⚠</div>
            <h1 style={{fontSize:'24px',fontWeight:'700',color:'#1e293b',marginBottom:'8px'}}>Something went wrong</h1>
            <p style={{color:'#64748b',marginBottom:'8px'}}>{this.state.error?.message || 'An unexpected error occurred'}</p>
            <p style={{color:'#94a3b8',fontSize:'12px',marginBottom:'24px'}}>{this.state.error?.stack?.split('\n')[1]?.trim() || ''}</p>
            <div style={{display:'flex',gap:'12px',justifyContent:'center'}}>
              <button onClick={() => window.location.reload()} style={{padding:'10px 24px',background:'#3b82f6',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',fontWeight:'600'}}>Reload Page</button>
              <button onClick={() => window.location.href='/gst-billing'} style={{padding:'10px 24px',background:'#f1f5f9',color:'#475569',border:'1px solid #e2e8f0',borderRadius:'8px',cursor:'pointer',fontWeight:'600'}}>Go to Billing</button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

import Login from './pages/Login';

// Lazy-loaded pages — only loaded when navigated to
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const CustomerList = lazy(() => import('./pages/CustomerList'));
const CustomerCreate = lazy(() => import('./pages/CustomerCreate'));
const ComplaintCreate = lazy(() => import('./pages/ComplaintCreate'));
const QuoteList = lazy(() => import('./pages/QuoteList'));
const QuoteCreate = lazy(() => import('./pages/QuoteCreate'));
const QuoteDetail = lazy(() => import('./pages/QuoteDetail'));
const JobList = lazy(() => import('./pages/JobList'));
const TaskManager = lazy(() => import('./pages/TaskManager'));
const JobCreate = lazy(() => import('./pages/JobCreate'));
const JobDetail = lazy(() => import('./pages/JobDetail'));
const SiteVisits = lazy(() => import('./pages/SiteVisits'));
const SchedulePage = lazy(() => import('./pages/SchedulePage'));
const InvoiceList = lazy(() => import('./pages/InvoiceList'));
const InvoiceCreate = lazy(() => import('./pages/InvoiceCreate'));
const InvoiceDetail = lazy(() => import('./pages/InvoiceDetail'));
const TechnicianPortal = lazy(() => import('./pages/TechnicianPortal'));
const ClientPortal = lazy(() => import('./pages/ClientPortal'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const ApprovalPage = lazy(() => import('./pages/ApprovalPage'));
const NotificationsModule = lazy(() => import('./pages/Notifications'));
const NotificationDetail = lazy(() => import('./pages/Notifications').then(m => ({ default: m.NotificationDetail })));
const WhatsAppLogs = lazy(() => import('./pages/WhatsAppLogs'));
const DLTTemplatesPage = lazy(() => import('./pages/DLTTemplatesPage'));
const AccountsHub = lazy(() => import('./pages/AccountsHub'));
const InvoiceCreatePage = lazy(() => import('./pages/InvoiceCreatePage'));
const NewPurchaseBill = lazy(() => import('./pages/NewPurchaseBill'));
const SerialSearch = lazy(() => import('./pages/SerialSearch'));
const SocietyAdmin = lazy(() => import('./pages/SocietyAdmin'));
const SocietyResident = lazy(() => import('./pages/SocietyResident'));
const SocietyAdminLogin = lazy(() => import('./pages/SocietyLogin').then(m => ({ default: m.SocietyAdminLogin })));
const SocietyResidentLogin = lazy(() => import('./pages/SocietyLogin').then(m => ({ default: m.SocietyResidentLogin })));
const QuoteBotPage = lazy(() => import('./pages/QuoteBotPage'));
const ChatbotAdmin = lazy(() => import('./pages/ChatbotAdmin'));

const PageLoader = () => (
  <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'60vh'}}>
    <div style={{textAlign:'center'}}>
      <div style={{width:32,height:32,border:'3px solid #e2e8f0',borderTopColor:'#3b82f6',borderRadius:'50%',animation:'spin 0.6s linear infinite',margin:'0 auto 12px'}} />
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auto-update: check version on every load, force refresh if new deploy detected
(async function checkForUpdates() {
  try {
    const resp = await fetch(`${API}/version`);
    if (!resp.ok) return;
    const { version } = await resp.json();
    const lastVersion = localStorage.getItem('app_version');
    
    if (lastVersion && lastVersion !== version) {
      console.log(`New version detected: ${lastVersion} → ${version}. Updating...`);
      localStorage.setItem('app_version', version);
      
      // Unregister old service worker
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        for (const reg of regs) { await reg.unregister(); }
      }
      // Clear all caches
      if ('caches' in window) {
        const names = await caches.keys();
        for (const name of names) { await caches.delete(name); }
      }
      // Force reload from server (skip browser cache)
      window.location.reload(true);
      return;
    }
    
    if (!lastVersion) {
      localStorage.setItem('app_version', version);
    }
  } catch (e) {}
})();

// Configure axios defaults for better performance
axios.defaults.timeout = 30000; // 30 second timeout
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Add request interceptor to include auth token (only for CCTV routes, skip if already set)
axios.interceptors.request.use(
  (config) => {
    // Don't override Authorization if already explicitly set (e.g., by Society module)
    if (config.headers.Authorization) {
      return config;
    }
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [showNotificationBanner, setShowNotificationBanner] = useState(false);
  const [showBatteryGuide, setShowBatteryGuide] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  // Listen for PWA install prompt and show banner (mobile only)
  useEffect(() => {
    localStorage.removeItem('pwaInstallDismissed');
    
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (isMobile && !sessionStorage.getItem('pwaInstallDismissed')) {
        setShowInstallBanner(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    // Show install banner only on mobile, not desktop
    if (isMobile && !isStandalone && !sessionStorage.getItem('pwaInstallDismissed')) {
      setShowInstallBanner(true);
    }
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Check if app is installed as PWA
  const isAppInstalled = () => {
    return window.matchMedia('(display-mode: standalone)').matches || 
           window.navigator.standalone === true;
  };

  // Auto-refresh for installed PWA to get latest updates
  const checkForUpdatesAndRefresh = () => {
    if (isAppInstalled()) {
      const lastRefreshTime = localStorage.getItem('pwaLastRefresh');
      const now = Date.now();
      
      // Refresh once per session (every 4 hours) to get latest updates
      if (!lastRefreshTime || (now - parseInt(lastRefreshTime)) > 4 * 60 * 60 * 1000) {
        localStorage.setItem('pwaLastRefresh', now.toString());
        
        // Register service worker and check for updates
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.getRegistration().then(reg => {
            if (reg) {
              reg.update().then(() => {
                console.log('Service worker updated');
              });
            }
          });
        }
      }
    }
  };

  // Show install banner after login if not installed (mobile only)
  const checkAndShowInstallBanner = () => {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (isMobile && !isAppInstalled() && !sessionStorage.getItem('pwaInstallDismissed')) {
      setShowInstallBanner(true);
    }
  };

  // Handle install button click (must be from user gesture)
  const handleInstallClick = async () => {
    // Check device type
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isAndroid = /Android/i.test(navigator.userAgent);
    
    if (deferredPrompt) {
      // Browser supports native install prompt (Chrome, Edge on Android)
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        console.log('PWA install outcome:', outcome);
        
        if (outcome === 'accepted') {
          localStorage.setItem('pwaInstalled', 'true');
        }
        setDeferredPrompt(null);
        setShowInstallBanner(false);
        sessionStorage.setItem('pwaInstallDismissed', 'true');
        return;
      } catch (error) {
        console.error('Install prompt error:', error);
      }
    }
    
    // Fallback instructions for different platforms
    if (isIOS) {
      alert('To install on iPhone/iPad:\n\n1. Tap the Share button (box with arrow at bottom)\n2. Scroll down and tap "Add to Home Screen"\n3. Tap "Add" in the top right\n\nThe app will appear on your home screen!');
    } else if (isAndroid) {
      alert('To install on Android:\n\n1. Tap the menu (3 dots) in your browser\n2. Tap "Install app" or "Add to Home Screen"\n3. Tap "Install"\n\nThe app will appear on your home screen!');
    } else {
      alert('To install this app:\n\n1. Open browser menu\n2. Look for "Install" or "Add to Home Screen"\n3. Follow the prompts\n\nThe app will appear on your home screen!');
    }
    
    // Only dismiss for this session — banner comes back next visit
    setShowInstallBanner(false);
    sessionStorage.setItem('pwaInstallDismissed', 'true');
  };

  // Dismiss install banner (session-only so it comes back next visit)
  const dismissInstallBanner = () => {
    setShowInstallBanner(false);
    sessionStorage.setItem('pwaInstallDismissed', 'true');
  };

  // Subscribe to push notifications
  const subscribeToPushNotifications = async () => {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        console.log('Push notifications not supported');
        return;
      }

      const currentPermission = Notification.permission;
      
      // If permission was denied, guide user to settings
      if (currentPermission === 'denied') {
        console.log('Notifications blocked by user');
        return;
      }

      // Request permission directly (no confirm gate — let the browser's own prompt handle it)
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.log('Notification permission not granted:', permission);
        return;
      }

      // Register service worker if not already registered
      let registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        registration = await navigator.serviceWorker.register('/service-worker.js');
        console.log('Service Worker registered');
      }

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      // Get VAPID public key from server
      const vapidResponse = await axios.get(`${API}/push/vapid-key`);
      const vapidPublicKey = vapidResponse.data.publicKey;
      
      if (!vapidPublicKey) {
        console.log('VAPID key not configured');
        return;
      }

      // Convert VAPID key to Uint8Array
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };

      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to server - use base64url encoding for keys
      const arrayBufferToBase64Url = (buffer) => {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        bytes.forEach(b => binary += String.fromCharCode(b));
        return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      };
      
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: arrayBufferToBase64Url(subscription.getKey('p256dh')),
          auth: arrayBufferToBase64Url(subscription.getKey('auth'))
        }
      }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      console.log('Push subscription saved to server');
    } catch (error) {
      console.error('Push subscription error:', error?.response?.data || error?.message || error);
    }
  };

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Subscribe to push notifications when user is logged in
  useEffect(() => {
    if (user && token) {
      subscribeToPushNotifications();
    }
  }, [user, token]);

  // Listen for messages from service worker (notification sound + navigation)
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    
    const handleSWMessage = (event) => {
      if (!event.data) return;
      
      // Handle push notification received (app is in foreground)
      if (event.data.type === 'PUSH_RECEIVED') {
        // Play notification sound
        try {
          const audio = new Audio('/notification-sound.wav');
          audio.volume = 1.0;
          audio.play().catch(() => {});
        } catch (e) {}
        
        // Show in-app toast with notification content
        const n = event.data.notification || {};
        const targetUrl = n.targetUrl;
        toast(n.title || 'Hayat Security', { 
          description: n.body || 'New notification',
          duration: 6000,
          action: targetUrl ? {
            label: 'Open',
            onClick: () => window.location.href = targetUrl
          } : undefined
        });
      }
      
      // Handle navigation request from service worker (notification click)
      if (event.data.type === 'NAVIGATE_TO' && event.data.url) {
        window.location.href = event.data.url;
      }
    };
    
    navigator.serviceWorker.addEventListener('message', handleSWMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleSWMessage);
    };
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    setToken(access_token);
    setUser(userData);
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    
    // Mark as fresh login for notification prompt
    sessionStorage.setItem('freshLogin', 'true');
    
    return userData;
  };

  // Check for fresh login and trigger notification + PWA prompts
  useEffect(() => {
    const isFreshLogin = sessionStorage.getItem('freshLogin');
    if (isFreshLogin && user && token) {
      sessionStorage.removeItem('freshLogin');
      
      setTimeout(async () => {
        const installed = isAppInstalled();
        
        if (installed) {
          checkForUpdatesAndRefresh();
        } else {
          checkAndShowInstallBanner();
        }
        
        // Always try to subscribe to push notifications on login
        await subscribeToPushNotifications();
        
        // Show battery/notification setup guide on mobile if not completed
        const isMobile = /Android/i.test(navigator.userAgent);
        if (isMobile && Notification.permission === 'granted' && !localStorage.getItem('notifSetupDone')) {
          setTimeout(() => {
            setShowBatteryGuide(true);
          }, 2000);
        }
      }, 1500);
    }
  }, [user, token]);

  // Also check for updates when app starts (for installed PWA)
  useEffect(() => {
    if (isAppInstalled()) {
      checkForUpdatesAndRefresh();
      
      // If notifications not enabled, show banner
      if (Notification.permission === 'default' && !localStorage.getItem('notificationBannerDismissed')) {
        setShowNotificationBanner(true);
      }
    }
  }, []);

  // Handle notification banner click
  const handleEnableNotifications = async () => {
    setShowNotificationBanner(false);
    await subscribeToPushNotifications();
  };

  // Dismiss notification banner
  const dismissNotificationBanner = () => {
    setShowNotificationBanner(false);
    localStorage.setItem('notificationBannerDismissed', 'true');
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    setToken(access_token);
    setUser(newUser);
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    return newUser;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('rememberedEmail');
    localStorage.removeItem('rememberedPassword');
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, showInstallBanner, handleInstallClick, dismissInstallBanner, showNotificationBanner, handleEnableNotifications, dismissNotificationBanner, showBatteryGuide, setShowBatteryGuide }}>
      {children}
    </AuthContext.Provider>
  );
};

const PrivateRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="text-cyan-400 text-lg">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/login" />;
  }

  return children;
};

function App() {
  return (
    <GlobalErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </AuthProvider>
    </GlobalErrorBoundary>
  );
}

// Separate component to use useAuth hooks
function AppContent() {
  const { showInstallBanner, handleInstallClick, dismissInstallBanner, showNotificationBanner, handleEnableNotifications, dismissNotificationBanner, showBatteryGuide, setShowBatteryGuide } = useAuth();
  
  return (
    <>
      {/* PWA Install Banner - persistent across all pages */}
      {showInstallBanner && (
        <div className="fixed top-0 left-0 right-0 bg-gradient-to-r from-cyan-600 to-teal-600 text-white p-3 z-[100] shadow-lg animate-slideDown">
          <div className="max-w-md mx-auto flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-lg shrink-0">📲</span>
              <span className="text-sm font-medium truncate">Install Hayat CCTV app for quick access!</span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={handleInstallClick}
                data-testid="pwa-install-banner-btn"
                className="bg-white text-cyan-700 px-4 py-1.5 rounded-full text-sm font-semibold hover:bg-cyan-50 transition-colors whitespace-nowrap"
              >
                Install
              </button>
              <button
                onClick={dismissInstallBanner}
                data-testid="pwa-install-dismiss-btn"
                className="text-white/80 hover:text-white p-1 text-lg leading-none"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Notification Permission Banner (for installed PWA) */}
      {showNotificationBanner && !showInstallBanner && (
        <div className="fixed top-0 left-0 right-0 bg-gradient-to-r from-amber-500 to-orange-500 text-white p-3 z-50 shadow-lg">
          <div className="max-w-md mx-auto flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xl">🔔</span>
              <span className="text-sm font-medium">Enable notifications for job updates!</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleEnableNotifications}
                className="bg-white text-amber-700 px-3 py-1 rounded-full text-sm font-semibold hover:bg-amber-50 transition-colors"
              >
                Enable
              </button>
              <button
                onClick={dismissNotificationBanner}
                className="text-white/80 hover:text-white p-1"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Notification Sound Setup Guide */}
      {showBatteryGuide && (
        <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4" onClick={() => setShowBatteryGuide(false)}>
          <div className="bg-white rounded-2xl max-w-sm w-full p-5 shadow-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-4">
              <div className="w-14 h-14 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl">🔊</span>
              </div>
              <h3 className="text-lg font-bold text-slate-800">Enable Notification Sound</h3>
              <p className="text-sm text-slate-500 mt-1">Get loud alerts for job updates</p>
            </div>
            
            <div className="space-y-4">
              <div className="bg-cyan-50 rounded-xl p-4">
                <p className="text-sm font-bold text-cyan-800 mb-2">Set Chrome Notification Sound:</p>
                <div className="text-sm text-slate-700 space-y-2">
                  <p><b>1.</b> Open phone <b>Settings</b></p>
                  <p><b>2.</b> Go to <b>Apps</b> → find <b>Chrome</b></p>
                  <p><b>3.</b> Tap <b>Notifications</b></p>
                  <p><b>4.</b> Find <b>"Sites"</b> or <b>"All sites"</b> category</p>
                  <p><b>5.</b> Make sure it's <b>ON</b></p>
                  <p><b>6.</b> Tap on it → set <b>Sound</b> to a loud tone</p>
                  <p><b>7.</b> Set <b>Importance</b> to <b>"Urgent"</b></p>
                </div>
              </div>
              
              <div className="bg-amber-50 rounded-xl p-4">
                <p className="text-sm font-bold text-amber-800 mb-2">For Background Alerts:</p>
                <div className="text-sm text-slate-700 space-y-2">
                  <p><b>1.</b> Settings → <b>Apps</b> → <b>Chrome</b> → <b>Battery</b></p>
                  <p><b>2.</b> Select <b>"Unrestricted"</b></p>
                </div>
              </div>
            </div>
            
            <button
              onClick={() => { setShowBatteryGuide(false); localStorage.setItem('notifSetupDone', 'true'); }}
              className="w-full mt-4 bg-cyan-600 hover:bg-cyan-500 text-white py-2.5 rounded-xl font-semibold text-sm transition-colors"
              data-testid="battery-guide-done-btn"
            >
              I've done this!
            </button>
            <button
              onClick={() => setShowBatteryGuide(false)}
              className="w-full mt-2 text-slate-400 hover:text-slate-600 py-1.5 text-sm transition-colors"
            >
              Remind me later
            </button>
          </div>
        </div>
      )}
      
      <div className="App">
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/approval/:token" element={<ApprovalPage />} />
            
            {/* Admin Routes */}
            <Route
              path="/dashboard"
              element={
                <PrivateRoute allowedRoles={['admin']}>
                  <AdminDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/change-password"
              element={
                <PrivateRoute allowedRoles={['admin', 'technician', 'client']}>
                  <ChangePassword />
                </PrivateRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <PrivateRoute allowedRoles={['admin', 'technician']}>
                  <NotificationsModule />
                </PrivateRoute>
              }
            />
            <Route
              path="/notifications/:id"
              element={
                <PrivateRoute allowedRoles={['admin', 'technician']}>
                  <NotificationDetail />
                </PrivateRoute>
              }
            />
            <Route
              path="/whatsapp-logs"
              element={
                <PrivateRoute allowedRoles={['admin']}>
                  <WhatsAppLogs />
                </PrivateRoute>
              }
            />
            <Route
              path="/dlt-templates"
              element={
                <PrivateRoute allowedRoles={['admin']}>
                  <DLTTemplatesPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/gst-billing"
              element={
                <PrivateRoute allowedRoles={['admin']}>
                  <AccountsHub />
                </PrivateRoute>
              }
            />
            <Route
              path="/gst-billing/new-invoice"
              element={
                <PrivateRoute allowedRoles={['admin']}>
                  <InvoiceCreatePage />
                </PrivateRoute>
              }
            />
            <Route
              path="/gst-billing/accounts"
              element={
                <PrivateRoute allowedRoles={['admin']}>
                  <Navigate to="/gst-billing?s=dashboard" replace />
                </PrivateRoute>
              }
            />
            <Route
              path="/gst-billing/quotations"
              element={
                <PrivateRoute allowedRoles={['admin']}>
                  <Navigate to="/gst-billing?s=quotations" replace />
                </PrivateRoute>
              }
            />
            <Route
              path="/purchase-module"
              element={
                <PrivateRoute allowedRoles={['admin']}>
                  <Navigate to="/gst-billing?s=purchase_bills" replace />
                </PrivateRoute>
              }
            />
            <Route
              path="/purchase-module/new"
              element={
                <PrivateRoute allowedRoles={['admin']}>
                  <NewPurchaseBill />
                </PrivateRoute>
              }
            />
            <Route
              path="/serial-search"
              element={
                <PrivateRoute allowedRoles={['admin']}>
                  <SerialSearch />
                </PrivateRoute>
              }
            />
            <Route
              path="/inventory"
              element={
                <PrivateRoute allowedRoles={['admin']}>
                  <Navigate to="/gst-billing?s=stock" replace />
                </PrivateRoute>
              }
            />
            <Route
              path="/customers"
              element={
                <PrivateRoute allowedRoles={['admin']}>
                  <CustomerList />
                </PrivateRoute>
              }
            />
            <Route
              path="/customers/create"
              element={
                <PrivateRoute allowedRoles={['admin']}>
                  <CustomerCreate />
                </PrivateRoute>
              }
            />
            <Route
              path="/complaints/create"
              element={
                <PrivateRoute allowedRoles={['admin']}>
                  <ComplaintCreate />
                </PrivateRoute>
              }
            />
            <Route
              path="/quotes"
              element={
                <PrivateRoute allowedRoles={['admin']}>
                  <QuoteList />
                </PrivateRoute>
              }
            />
            <Route
              path="/quotes/create"
              element={
                <PrivateRoute allowedRoles={['admin']}>
                  <QuoteCreate />
                </PrivateRoute>
              }
            />
            <Route
              path="/quotes/:id"
              element={
                <PrivateRoute allowedRoles={['admin', 'client']}>
                  <QuoteDetail />
                </PrivateRoute>
              }
            />
            <Route
              path="/jobs"
              element={
                <PrivateRoute allowedRoles={['admin', 'technician']}>
                  <JobList />
                </PrivateRoute>
              }
            />
            <Route
              path="/tasks"
              element={
                <PrivateRoute allowedRoles={['admin', 'technician']}>
                  <TaskManager />
                </PrivateRoute>
              }
            />

            <Route
              path="/jobs/create"
              element={
                <PrivateRoute allowedRoles={['admin']}>
                  <JobCreate />
                </PrivateRoute>
              }
            />
            <Route
              path="/jobs/:id"
              element={
                <PrivateRoute allowedRoles={['admin', 'technician', 'client']}>
                  <JobDetail />
                </PrivateRoute>
              }
            />
            <Route
              path="/site-visits"
              element={
                <PrivateRoute allowedRoles={['admin', 'technician']}>
                  <SiteVisits />
                </PrivateRoute>
              }
            />
            <Route
              path="/schedule"
              element={
                <PrivateRoute allowedRoles={['admin']}>
                  <SchedulePage />
                </PrivateRoute>
              }
            />
            <Route
              path="/invoices"
              element={
                <PrivateRoute allowedRoles={['admin', 'client']}>
                  <InvoiceList />
                </PrivateRoute>
              }
            />
            <Route
              path="/invoices/create"
              element={
                <PrivateRoute allowedRoles={['admin']}>
                  <InvoiceCreate />
                </PrivateRoute>
              }
            />
            <Route
              path="/invoices/:id"
              element={
                <PrivateRoute allowedRoles={['admin', 'client']}>
                  <InvoiceDetail />
                </PrivateRoute>
              }
            />
            
            {/* Technician Routes */}
            <Route
              path="/technician"
              element={
                <PrivateRoute allowedRoles={['technician']}>
                  <TechnicianPortal />
                </PrivateRoute>
              }
            />
            
            {/* Client Routes */}
            <Route
              path="/client"
              element={
                <PrivateRoute allowedRoles={['client']}>
                  <ClientPortal />
                </PrivateRoute>
              }
            />

            {/* Society Module - Separate Auth System */}
            <Route path="/society/admin" element={<SocietyAdminLogin />} />
            <Route path="/society/login" element={<SocietyResidentLogin />} />
            <Route path="/society/admin/dashboard" element={<SocietyAdmin />} />
            <Route path="/society/resident/dashboard" element={<SocietyResident />} />
            <Route path="/society" element={<SocietyResidentLogin />} />
            {/* Chatbot — public routes */}
            <Route path="/chat" element={<QuoteBotPage />} />
            <Route path="/quote" element={<QuoteBotPage />} />
            <Route path="/get-quote" element={<QuoteBotPage />} />
            <Route path="/workask" element={<QuoteBotPage />} />
            {/* Chatbot — admin */}
            <Route path="/chatbot-admin" element={<PrivateRoute allowedRoles={['admin']}><ChatbotAdmin /></PrivateRoute>} />
            <Route path="/chatbot" element={<PrivateRoute allowedRoles={['admin']}><ChatbotAdmin /></PrivateRoute>} />
            <Route path="/admin/workask" element={<PrivateRoute allowedRoles={['admin']}><ChatbotAdmin /></PrivateRoute>} />
          </Routes>
          </Suspense>
          <Toaster position="top-right" theme="dark" />
        </div>
      </>
  );
}

export default App;
export { API, BACKEND_URL };
